import { useCallback, useSyncExternalStore } from 'react';

/**
 * Reads and writes a single query-string parameter.
 *
 * The selected khutbah and reading language belong in the URL, not in
 * component state: a reader sending someone "this khutbah in Malayalam" must
 * be sending a working link, and the back button has to work. That is the
 * frontend standards' rule about shareable navigation state.
 *
 * Built on `useSyncExternalStore` for the same reason the theme provider is —
 * the URL is an external store, and subscribing to it properly is what makes
 * the back button work without an effect that mirrors state.
 *
 * No router dependency. One parameter pair does not justify one, and adding a
 * router now would decide the routing shape before the screens that need it
 * exist.
 */

/** Notifies subscribers after a programmatic `pushState`, which fires no event. */
const listeners = new Set<() => void>();

function notify() {
  for (const listener of listeners) {
    listener();
  }
}

function subscribe(onChange: () => void) {
  listeners.add(onChange);
  // popstate covers the back and forward buttons; the set covers our own writes.
  window.addEventListener('popstate', onChange);
  return () => {
    listeners.delete(onChange);
    window.removeEventListener('popstate', onChange);
  };
}

export function useUrlParam(
  key: string,
  fallback: string,
): [string, (value: string) => void] {
  const value = useSyncExternalStore(
    subscribe,
    () => new URLSearchParams(window.location.search).get(key) ?? fallback,
    // Server snapshot: there is no URL during prerender, so the fallback is
    // the only honest answer.
    () => fallback,
  );

  const setValue = useCallback(
    (next: string) => {
      const params = new URLSearchParams(window.location.search);
      if (next === fallback) {
        // Keep the default out of the URL so a shared link stays legible.
        params.delete(key);
      } else {
        params.set(key, next);
      }
      const query = params.toString();
      window.history.pushState(
        null,
        '',
        query ? `${window.location.pathname}?${query}` : window.location.pathname,
      );
      notify();
    },
    [key, fallback],
  );

  return [value, setValue];
}
